import { type ShiftAuction } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, CardActionArea, Stack, type SxProps, type Theme } from "@mui/material";
import { type RecentColleague } from "@src/appV2/Agents/api/useGetRecentColleagues";
import { type DateRange } from "@src/appV2/lib/Calendar";
import { mergeSxProps } from "@src/appV2/redesign/system/ui-components";
import { type ForwardedRef, forwardRef } from "react";

import { WorkTogetherStack } from "../../WorkWithFriends/components/WorkTogetherStack";
import { type WorkerPublicProfile } from "../../WorkWithFriends/hooks/useGetWorkerPublicProfile";
import { ShortLeadTimePill } from "../ArrivalTime/Pill";
import { ShiftBundleFooter } from "../Bundle/Footer";
import { type ShiftBundle } from "../Bundle/types";
import { estimateBundleTotalPayInMinorUnits } from "../Bundle/utils";
import { ShiftCardWrapper } from "../Card/Wrapper";
import { BookingCardContentWrapper } from "../CardContentWrapper";
import { DOUBLE_BORDER_COLOR } from "../Double/constants";
import { ShiftDoubleFooter } from "../Double/Footer";
import { type ShiftDouble } from "../Double/types";
import { type PayBoostEligibilityResult } from "../PayBoost/getPayBoostEligibility";
import { ShiftCardPillsWrapper } from "../PillsWrapper";
import { ShiftQualificationLabel } from "../QualificationLabel";
import { BookingCardSummaryWrapper } from "../SummaryWrapper";
import { TimeSlotPill } from "../TimeSlotPill";
import { ShiftUnavailablePill } from "../UnavailablePill";
import { ShiftCardSummaryInfo } from "./CardSummaryInfo";
import { type OpenShiftCardPillsProps } from "./getOpenShiftCardPillsProps";
import { OpenShiftCardPills } from "./Pills";
import { OpenShiftPayInfoLabel } from "./ShiftPayInfoLabel";
import type { Offer, OpenShift, OpenShiftWorkplace, TimeSlot } from "./types";

interface OnClickParams {
  shift: OpenShift;
  workplace: OpenShiftWorkplace;
  offer?: Offer;
}

interface OpenShiftCardProps {
  shift: OpenShift;
  workers: WorkerPublicProfile[];
  workplace: OpenShiftWorkplace;
  onClick?: (params: OnClickParams) => void;
  onDoubleClick?: () => void;
  sx?: SxProps<Theme>;
  friendsMap?: Map<string, WorkerPublicProfile>;
  recentColleaguesMap?: Map<string, RecentColleague>;

  // Presentational props - derived data passed in
  timeSlot: TimeSlot;
  dateRange: DateRange;
  formattedDistance?: string;
  disabled: boolean;
  isFavoriteWorkplace?: boolean;
  offer?: Offer;
  offerAmount?: number;
  shiftAuction?: ShiftAuction;
  availableShiftBoostAmount?: number;
  payBoostEligibility?: PayBoostEligibilityResult;
  offerIsError: boolean;
  offerIsLoading: boolean;
  qualificationText?: string;
  isLoadingQualification: boolean;
  pillsProps: OpenShiftCardPillsProps;
  isWorkWithFriendsV2Enabled: boolean;
  remainingWorkDurationInHours: number;
  remainingTotalDurationInHours: number;
  breakDurationInMinutes: number;
  requiresBreak: boolean;
  showBreakInfo?: boolean;
  isShortLeadTime: boolean;
  shouldLeaveNow: boolean;
  minutesElapsed?: number;
  bundle?: ShiftBundle;
  double?: ShiftDouble;
  isForYouShift?: boolean;
}

/**
 * Presentational component for an open shift card.
 * Displays an open shift card with all shift details, pay information, qualifications, and action pills.
 */
function OpenShiftCardBase(props: OpenShiftCardProps, ref: ForwardedRef<HTMLButtonElement>) {
  const {
    shift,
    workplace,
    onClick,
    onDoubleClick,
    workers,
    sx,
    friendsMap,
    recentColleaguesMap,
    timeSlot,
    dateRange,
    formattedDistance,
    disabled,
    offer,
    offerAmount,
    shiftAuction,
    availableShiftBoostAmount,
    payBoostEligibility,
    offerIsError,
    offerIsLoading,
    qualificationText,
    isLoadingQualification,
    pillsProps,
    isWorkWithFriendsV2Enabled,
    isShortLeadTime,
    shouldLeaveNow,
    remainingWorkDurationInHours,
    remainingTotalDurationInHours,
    breakDurationInMinutes,
    requiresBreak,
    showBreakInfo = false,
    minutesElapsed = 0,
    isFavoriteWorkplace,
    bundle,
    double,
    isForYouShift,
  } = props;

  const anchorShiftPayInMinorUnits = isDefined(offer)
    ? Math.round(offer.attributes.pay.amountInMinorUnits * remainingWorkDurationInHours)
    : undefined;

  const estimatedBundleTotalPayInMinorUnits =
    isDefined(anchorShiftPayInMinorUnits) && isDefined(bundle)
      ? estimateBundleTotalPayInMinorUnits(anchorShiftPayInMinorUnits, bundle.totalShiftCount)
      : undefined;

  const isDouble = isDefined(double) && !isDefined(bundle);
  const hasFooter = isDefined(bundle) || isDouble;

  return (
    <ShiftCardWrapper
      sx={mergeSxProps(
        { display: "flex", flexDirection: "column", opacity: disabled ? 0.5 : 1 },
        sx
      )}
      data-testid={`shift-card-${shift.id}`}
      isUrgent={shift.attributes.isUrgent}
      isBundle={isDefined(bundle)}
      isDouble={isDouble}
      isForYouShift={isForYouShift}
    >
      <CardActionArea
        ref={ref}
        sx={{
          flexGrow: 1,
          ...(hasFooter && {
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
          }),
        }}
        disabled={disabled}
        onClick={() => onClick?.({ shift, workplace, offer })}
      >
        <Box
          sx={(theme) => ({
            flexGrow: 1,
            ...(hasFooter && {
              marginTop: "-1px",
              marginLeft: "-1px",
              marginRight: "-1px",
              boxShadow: theme.shadows[3],
              borderRadius: theme.borderRadius?.medium,
            }),
            ...(isDefined(bundle) && {
              border: `1px solid ${theme.palette.shift?.bundle?.border}`,
            }),
            ...(isDouble && {
              border: `1px solid ${DOUBLE_BORDER_COLOR}`,
            }),
            ...(hasFooter &&
              isForYouShift && {
                background: (theme) => theme.palette?.shift?.forYou.background,
                border: (theme) => theme.palette.shift?.forYou.border,
              }),
          })}
        >
          <BookingCardContentWrapper sx={{ gap: 1 }}>
            <BookingCardSummaryWrapper sx={{ justifyContent: "space-between" }}>
              <ShiftCardPillsWrapper>
                {isShortLeadTime ? (
                  <ShortLeadTimePill shouldLeaveNow={shouldLeaveNow} />
                ) : (
                  <TimeSlotPill timeSlot={timeSlot} />
                )}

                <OpenShiftCardPills {...pillsProps} />
              </ShiftCardPillsWrapper>

              <OpenShiftPayInfoLabel
                isLoading={offerIsLoading}
                offerPay={offerAmount}
                finalPay={offerAmount}
                originalAmount={
                  isDefined(offerAmount) ? remainingWorkDurationInHours * offerAmount : undefined
                }
                shiftBoost={availableShiftBoostAmount}
                shiftAuction={shiftAuction?.attributes}
                payBoostEligibility={payBoostEligibility}
              />
            </BookingCardSummaryWrapper>

            <ShiftCardSummaryInfo
              sx={{ flex: 1 }}
              dateRange={dateRange}
              isFavoriteWorkplace={isFavoriteWorkplace}
              timezone={workplace.attributes.location.timezone}
              deviceTimezone={new Intl.DateTimeFormat().resolvedOptions().timeZone}
              durationInHours={remainingTotalDurationInHours}
              breakDurationInMinutes={breakDurationInMinutes}
              requiresBreak={requiresBreak}
              workplaceName={workplace.attributes.name}
              formattedDistance={formattedDistance}
              showBreakInfo={showBreakInfo}
              minutesElapsed={minutesElapsed}
            />

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={3}
              sx={{ marginTop: 3, minHeight: (theme) => theme.size?.avatar.small }}
            >
              <ShiftQualificationLabel
                qualificationText={qualificationText ?? ""}
                isLoading={isLoadingQualification}
              />

              {offerIsError && <ShiftUnavailablePill />}
              {!offerIsError && isWorkWithFriendsV2Enabled && (
                <WorkTogetherStack
                  workers={workers}
                  friendsMap={friendsMap}
                  recentColleaguesMap={recentColleaguesMap}
                />
              )}
            </Stack>
          </BookingCardContentWrapper>
        </Box>

        {!offerIsError && isDefined(bundle) && isDefined(estimatedBundleTotalPayInMinorUnits) && (
          <ShiftBundleFooter
            requiredShiftCount={bundle.requiredShiftCount}
            totalShiftCount={bundle.totalShiftCount}
            totalPayInMinorUnits={estimatedBundleTotalPayInMinorUnits}
          />
        )}
      </CardActionArea>

      {!offerIsError && isDouble && (
        <ShiftDoubleFooter
          companionCount={double?.companionShifts.length}
          onClick={onDoubleClick}
        />
      )}
    </ShiftCardWrapper>
  );
}

export const OpenShiftCard = forwardRef(OpenShiftCardBase);
